import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import type { Palette, PaletteMode } from '@mui/material';
import { AppScene } from './contexts/AppContext';
import { ThemePreferenceProvider, useThemePreference } from './contexts/ThemePreferenceContext';
import { getThemeTokens } from './utils/appThemes';
import { syncPaletteWithTokens } from './utils/paletteSync';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { dateLocales } from '@/i18n/dateLocales';
import { muiLocales } from '@/i18n/muiLocales';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { useActivePersona } from './personas/useActivePersona';
import AuthGuard from './router/AuthGuard';
import { TimeZoneScene } from './contexts/TimeZoneScene';
import { BrowserRouter } from 'react-router-dom';

/**
 * MUI-temat byggs av det tema ThemePreferenceProvider har löst upp mot aktiv persona
 * — samma värde som allt annat som målar färger läser (Layout, Navbar, FAB, vyerna).
 * Tidigare gjorde App sin EGEN upplösning av persona-temat och sin egen dark
 * mode-härledning, vilket gjorde att MUI-paletten kunde byta persona medan
 * token-baserade ytor låg kvar i den förra vyns färger. Därför en inre komponent:
 * temat måste läsas ur providern, inte räknas ut parallellt med den.
 */
const AppThemed = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const { selectedTheme, isDarkMode } = useThemePreference();
  const profile = useActivePersona();

  // Ladda persona-typsnitt endast när det behövs, så vuxenvyns PWA-precache inte växer.
  // Barn → Nunito (som tidigare). Teen → Inter (de vikter teen-temat/MUI-defaults använder).
  useEffect(() => {
    if (profile.persona === 'child') {
      import('@fontsource/nunito/index.css');
    } else if (profile.persona === 'teen') {
      // 400 (body/regular), 500 (MUI medium/knapp-default), 600 (designdokets
      // betoningsvikt §6), 700 (bold/rubriker). Laddas dynamiskt likt Nunito.
      import('@fontsource/inter/400.css');
      import('@fontsource/inter/500.css');
      import('@fontsource/inter/600.css');
      import('@fontsource/inter/700.css');
    }
  }, [profile.persona]);

  const theme = useMemo(() => {
    const mode: PaletteMode = isDarkMode ? 'dark' : 'light';
    const baseTheme = profile.theme.buildTheme(mode);

    const tk = getThemeTokens(selectedTheme, isDarkMode);
    const synced = syncPaletteWithTokens(baseTheme.palette, tk, mode, profile.persona);

    const merged = createTheme({
      ...baseTheme,
      palette: { ...baseTheme.palette, ...synced },
    });

    // Augmenterade custom palette-props (deklarerade via module augmentation på
    // Palette, inte på PaletteOptions — de kan därför inte typas via createTheme).
    const palette = merged.palette as Palette & { calendarLine: string };
    palette.line = synced.line;
    palette.lineLight = synced.lineLight;
    palette.lineDark = synced.lineDark;
    palette.white = synced.white;
    palette.black = synced.black;
    palette.backdrop = synced.backdrop;
    palette.calendarLine = synced.calendarLine;
    palette.weekdays = synced.weekdays;
    palette.activities = synced.activities;
    palette.child = synced.child;
    palette.teen = synced.teen;

    return merged;
  }, [isDarkMode, profile, selectedTheme]);

  return (
    <AppScene>
      <TimeZoneScene>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={dateLocales[lang]}
          localeText={muiLocales[lang]}
        >
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <BrowserRouter>
              <AuthGuard />
            </BrowserRouter>
          </ThemeProvider>
        </LocalizationProvider>
      </TimeZoneScene>
    </AppScene>
  );
};

export const App = () => (
  <ThemePreferenceProvider>
    <AppThemed />
  </ThemePreferenceProvider>
);
